/**
 * The platform's technology card, as an accordion (the legacy card is
 * components/TechCard.tsx, kept and switchable from Admin → Tech URL pages).
 *
 * Collapsed: title; "University · Country"; TRL and relevance pills; a
 * chevron; a one-line summary. Expanded (thin brown border): segmented tabs
 * Summary | Researchers & IP | Problem & Applications, then the footer —
 * Contact University, Get IP Intel Report, share, bookmark.
 *
 * The collapsed header renders from what the list already has (`preview`);
 * the body comes from GET /api/v1/tech-cards/{id}, fetched on first expand
 * and cached for the tab. The server decides what this viewer may see: a
 * locked section arrives without data and shows blurred empty lines under a
 * sign-in / upgrade button, a hidden one is absent.
 *
 * `variant="modal"` is the same card, always expanded, with a close button —
 * App's tech-card modal renders it when the design switch says accordion.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { apiService } from '../../services/apiService';
import { useUpgradeGate } from '../useUpgradeGate';
import {
  CardPreview, CardSection, clearTechCardCache, fetchTechCard, patchCachedTechCard, peekTechCard, SimilarCardItem, storedUserId,
  TechCardPayload, trlPillOf,
} from './api';
import { IpIntelOrderModal } from './IpIntelOrderModal';
import { ShareMenu } from './ShareMenu';
import { useTechCardStyles } from './styles';
import { tcTrack } from './track';

export type CardTab = 'summary' | 'researchers_ip' | 'problem_applications';

const TAB_LABELS: Record<CardTab, string> = {
  summary: 'Summary',
  researchers_ip: 'Researchers & IP',
  problem_applications: 'Problem & Applications',
};

/** What a locked section is called in its button ("Sign up to view the …"). */
const SECTION_NOUN: Record<string, string> = {
  summary: 'summary',
  summary_short: 'full summary',
  trl: 'readiness level',
  value_levers: 'value levers',
  researchers_ip: 'researchers & IP',
  problem_applications: 'problem & applications',
  similar_tech: 'similar technologies',
};

/** What the parent needs to open the contact flow for this tech. */
export interface CardContactTarget {
  id: string;
  title: string;
  university: string;
  country: string;
}

export interface TechAccordionCardProps {
  techId: string;
  /** Header data a list already has, shown before the payload arrives. */
  preview?: CardPreview;
  /** "Relevance: 83%" — an AI relevance score or a keyword match (see relevance.ts). */
  relevance?: number | null;
  /** What the relevance is, for the pill's tooltip. */
  relevanceKind?: 'ai' | 'keyword' | null;
  variant?: 'list' | 'modal';
  /** List: controlled open state (one open at a time is the parent's call). */
  expanded?: boolean;
  onToggle?: (open: boolean) => void;
  /** Tracking: 1-based position in the list, and where the card sits. */
  rank?: number;
  surface?: string;
  /** Signed-out rendering: bookmark and locked sections prompt a login.
   *  Defaults to "no stored session". */
  loggedOut?: boolean;
  onLoginPrompt?: (reason: string) => void;
  onContact?: (target: CardContactTarget) => void;
  contactLabel?: string;
  contactDisabled?: boolean;
  /** The parent's saved state (e.g. one getSavedCards for the whole list). */
  bookmarked?: boolean;
  onBookmarkChange?: (techId: string, on: boolean) => void;
  /** Similar technologies open here (in the platform) when given. */
  onOpenTech?: (techId: string) => void;
  onUniversityClick?: (target: { code?: string; name: string }) => void;
  onCountryClick?: (country: string) => void;
  /** Modal variant. */
  onClose?: () => void;
  /** Modal variant, after moving to a similar technology: back to the one
   *  before (its title labels the button). */
  onBack?: () => void;
  backLabel?: string;
  /** The payload could not be loaded (the modal falls back to the legacy card). */
  onLoadError?: (status: number) => void;
}

// ---------------------------------------------------------------------------
// Small pieces
// ---------------------------------------------------------------------------

const ChevronIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const BookmarkIcon: React.FC<{ filled?: boolean }> = ({ filled }) => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 3h12a1 1 0 0 1 1 1v17l-7-4.5L5 21V4a1 1 0 0 1 1-1z" />
  </svg>
);

const LockGlyph: React.FC<{ size?: number }> = ({ size = 10 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </svg>
);

/** Blurred placeholder lines — a locked section never receives its text. */
const Locked: React.FC<{ lines?: number; label: string; onClick: () => void }> = ({ lines = 4, label, onClick }) => (
  <div className="tcx-locked">
    <div className="tcx-filler" aria-hidden="true">
      {Array.from({ length: lines }, (_, i) => (
        <span key={i} style={{ width: i === lines - 1 ? '55%' : `${88 + ((i * 7) % 12)}%` }} />
      ))}
    </div>
    <div className="tcx-lock-veil">
      <button type="button" className="tcx-lock-btn" onClick={e => { e.stopPropagation(); onClick(); }}>
        <LockGlyph size={13} /><span>{label}</span>
      </button>
    </div>
  </div>
);

function useCardToast(): [React.ReactNode, (msg: string) => void] {
  const [msg, setMsg] = useState('');
  const timer = useRef<number | null>(null);
  const show = useCallback((m: string) => {
    setMsg(m);
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setMsg(''), 2600);
  }, []);
  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current); }, []);
  const el = msg && typeof document !== 'undefined'
    ? createPortal(<div className="tcx tcx-toast" role="status" aria-live="polite">{msg}</div>, document.body)
    : null;
  return [el, show];
}

/** The technical tag only ("Higher Process Yield"), never "Lever · Tag"; a tag shared by two levers shows once. */
const leverTags = (items: any[]): string[] => {
  const seen = new Set<string>();
  return (items || [])
    .map(it => (typeof it === 'string' ? (it.split(/\s*(?:->|→|·|:)\s*/).pop() || it) : (it?.tag || it?.label || it?.lever || '')))
    .map((t: string) => String(t || '').trim())
    .filter((t: string) => t && !seen.has(t.toLowerCase()) && !!seen.add(t.toLowerCase()));
};

const firstSentence = (text: string): string => {
  const t = (text || '').trim();
  const m = t.match(/^[\s\S]*?[.!?](?=\s|$)/);
  return m ? m[0] : t;
};

// ---------------------------------------------------------------------------
// The card
// ---------------------------------------------------------------------------

export const TechAccordionCard: React.FC<TechAccordionCardProps> = props => {
  const {
    techId, preview, relevance, relevanceKind, variant = 'list', rank, surface = variant === 'modal' ? 'modal' : 'search',
    onToggle, onLoginPrompt, onContact, contactLabel, contactDisabled, onBookmarkChange, onOpenTech,
    onUniversityClick, onCountryClick, onClose, onBack, backLabel, onLoadError,
  } = props;
  useTechCardStyles();
  const upgrade = useUpgradeGate();
  const isModal = variant === 'modal';
  const open = isModal ? true : !!props.expanded;
  const loggedOut = props.loggedOut ?? !storedUserId();

  const [data, setData] = useState<TechCardPayload | null>(() => peekTechCard(techId));
  const [loadState, setLoadState] = useState<'idle' | 'loading' | 'ready' | 'error'>(() => (peekTechCard(techId) ? 'ready' : 'idle'));
  const [loadError, setLoadError] = useState('');
  const [tab, setTab] = useState<CardTab>('summary');
  const [saved, setSaved] = useState<boolean | null>(props.bookmarked ?? null);
  const [saving, setSaving] = useState(false);
  const [ipOpen, setIpOpen] = useState(false);
  // Escape closes the modal card — but not while its own share menu or the
  // IP Intel sheet is up: Escape there closes that one first.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    if (!isModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented || ipOpen || document.querySelector('.tcx.tcx-menu')) return;
      tcTrack('techcard_close', { tech_id: techId, surface, via: 'escape' });
      onCloseRef.current?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isModal, ipOpen, techId, surface]);
  const [toast, showToast] = useCardToast();
  const rootRef = useRef<HTMLDivElement>(null);
  const cssId = techId.replace(/[^A-Za-z0-9_-]/g, '_');
  const idRef = useRef(techId);
  idRef.current = techId;

  // A different tech in the same slot (the modal's similar-tech hop).
  useEffect(() => {
    const hit = peekTechCard(techId);
    setData(hit);
    setLoadState(hit ? 'ready' : 'idle');
    setLoadError('');
    setTab('summary');
  }, [techId]);

  useEffect(() => { if (props.bookmarked !== undefined) setSaved(props.bookmarked); }, [props.bookmarked]);

  const load = useCallback((force = false) => {
    const id = techId;
    setLoadState('loading');
    setLoadError('');
    fetchTechCard(id, { force }).then(res => {
      if (idRef.current !== id) return;
      if (res.ok && res.data) { setData(res.data); setLoadState('ready'); return; }
      setLoadState('error');
      setLoadError(res.error);
      tcTrack('techcard_load_error', { tech_id: id, status: res.status, surface });
      onLoadError?.(res.status);
    });
  }, [techId, surface, onLoadError]);

  useEffect(() => {
    if (open && loadState === 'idle') load();
  }, [open, loadState, load]);

  // The plan changed (paid here via the pricing modal, a trial started):
  // cached cards were cut for the old plan, so drop them and, if this card
  // has data, fetch what the new plan may see. The server re-reads the plan
  // for every card request (resolve_viewer fresh), so no stale paywall.
  const paid = !!upgrade.ent?.paid;
  const lastPaid = useRef(paid);
  useEffect(() => {
    if (lastPaid.current === paid) return;
    lastPaid.current = paid;
    clearTechCardCache();
    if (data || open) load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paid]);

  // Opening a card below one that collapses moves it up the page: bring its
  // header back into view.
  const wasOpen = useRef(open);
  useEffect(() => {
    if (!isModal && open && !wasOpen.current) {
      requestAnimationFrame(() => {
        const r = rootRef.current?.getBoundingClientRect();
        if (r && (r.top < 56 || r.top > window.innerHeight - 120)) rootRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      });
    }
    wasOpen.current = open;
  }, [open, isModal]);

  // ---- derived header ----
  const uni = data?.university || {};
  const title = data?.title || preview?.title || 'Untitled technology';
  const uniName = uni.label || uni.name || preview?.university || '';
  const country = uni.country || preview?.country || '';
  const trlPill = data?.trl?.pill || trlPillOf(preview?.trl);
  // No pill at all when the TRL is not disclosed/found ("TRL —" is never shown);
  // the expanded card still explains it in the TRL block.
  const trlKnown = trlPill !== 'TRL —' && (data?.trl ? typeof data.trl.level === 'number' : true);
  const relPct = typeof relevance === 'number' && isFinite(relevance) ? Math.max(0, Math.min(100, Math.round(relevance))) : null;
  const summarySec = data?.sections?.find(s => s.id === 'summary');
  const oneLine = preview?.summary || (summarySec?.data?.text ? firstSentence(String(summarySec.data.text)) : '');
  const ctas = data?.ctas || { contact_university: true, ip_assessment: true, share: true, bookmark: true };
  const isSaved = saved ?? !!data?.bookmarked;

  const section = (id: string): CardSection | undefined =>
    (data?.sections || []).find(s => String(s.id) === id && s.mode !== 'hidden');

  const tabs: CardTab[] = ['summary'];
  if (section('researchers_ip')) tabs.push('researchers_ip');
  if (section('problem_applications')) tabs.push('problem_applications');
  const activeTab: CardTab = tabs.includes(tab) ? tab : 'summary';

  // ---- actions ----
  const toggle = () => {
    if (isModal) return;
    const next = !open;
    tcTrack(next ? 'techcard_expand' : 'techcard_collapse', { tech_id: techId, rank: rank ?? null, surface });
    onToggle?.(next);
  };

  const promptLogin = (reason: string) => {
    tcTrack('techcard_login_prompt', { reason, tech_id: techId, surface });
    if (onLoginPrompt) onLoginPrompt(reason);
    else window.location.href = '/';
  };

  const onLocked = (sectionId: string, lock?: CardSection['lock']) => {
    const reason = lock?.reason || (loggedOut || data?.state === 'pre_login' ? 'sign_in' : 'upgrade');
    tcTrack('techcard_locked_click', { section: sectionId, reason, tech_id: techId, surface });
    if (reason === 'sign_in') promptLogin(`locked_${sectionId}`);
    // Already paid (the lock is from before an upgrade): refetch, don't paywall.
    else if (upgrade.ent?.paid) { clearTechCardCache(); load(true); }
    else upgrade.gate(false, `the technology's ${SECTION_NOUN[sectionId] || 'details'}`);
  };

  const lockLabel = (sectionId: string, lock?: CardSection['lock']) => {
    if (lock?.message) return lock.message;
    const noun = SECTION_NOUN[sectionId] || 'this section';
    const reason = lock?.reason || (loggedOut || data?.state === 'pre_login' ? 'sign_in' : 'upgrade');
    return reason === 'sign_in' ? (noun === 'summary' ? 'Sign up to view full summary' : `Sign up to view the ${noun}`) : `Upgrade to unlock the ${noun}`;
  };

  const contact = () => {
    // `locked` for signed-in readers too (an Explorer's click opens the plans
    // sheet), so the funnel can split locked from unlocked contact clicks.
    tcTrack('techcard_contact', { tech_id: techId, surface, locked: loggedOut || !upgrade.ent?.can_contact });
    if (loggedOut) { promptLogin('contact_university'); return; }
    onContact?.({ id: data?.id || techId, title, university: uniName, country });
  };

  const openIpIntel = () => {
    tcTrack('techcard_ipintel_open', { tech_id: techId, surface });
    setIpOpen(true);
  };

  const toggleBookmark = async () => {
    if (loggedOut) {
      tcTrack('techcard_locked_click', { section: 'bookmark', reason: 'sign_in', tech_id: techId, surface });
      promptLogin('bookmark');
      return;
    }
    const uid = storedUserId();
    if (saving || !uid) return;
    const next = !isSaved;
    setSaved(next);
    setSaving(true);
    const id = data?.id || techId;
    let ok = false;
    try {
      const res = await apiService.saveCard({ asset_type: 'technology', asset_id: id, bookmarked: next, liked: null, linked_chat_id: null }, uid);
      ok = !!res.success;
    } catch { ok = false; }
    setSaving(false);
    if (!ok) { setSaved(!next); showToast('Could not update your bookmark — please try again.'); return; }
    tcTrack('techcard_bookmark', { on: next, tech_id: id, surface });
    patchCachedTechCard(id, { bookmarked: next });
    onBookmarkChange?.(id, next);
    showToast(next ? 'Saved to your bookmarks' : 'Removed from your bookmarks');
  };

  const pickTab = (t: CardTab) => {
    if (t === activeTab) return;
    tcTrack('techcard_tab', { tab: t, tech_id: techId, surface });
    setTab(t);
  };

  const onTabKey = (e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const i = tabs.indexOf(activeTab);
    const next = tabs[(i + (e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
    pickTab(next);
    requestAnimationFrame(() => rootRef.current?.querySelector<HTMLElement>(`#tcx-tab-${cssId}-${next}`)?.focus());
  };

  // ---- section bodies ----
  const renderSummaryTab = () => {
    const sum = section('summary');
    const trlSec = section('trl');
    const levers = section('value_levers');
    const similar = section('similar_tech');
    const trl = data?.trl;
    return (
      <>
        {sum && (sum.mode === 'locked' ? (
          <Locked lines={4} label={lockLabel('summary', sum.lock)} onClick={() => onLocked('summary', sum.lock)} />
        ) : (() => {
          const text = String(sum.data?.text || '').trim();
          if (!text) return <p className="tcx-muted">No summary has been published for this technology yet.</p>;
          if (sum.data?.is_short) {
            return (
              <>
                <div className="tcx-fade"><p className="tcx-p">{text}</p></div>
                <button type="button" className="tcx-upsell" onClick={() => onLocked('summary_short', { reason: loggedOut || data?.state === 'pre_login' ? 'sign_in' : 'upgrade' })}>
                  <LockGlyph size={12} />
                  {loggedOut || data?.state === 'pre_login' ? 'Sign in to read the full summary' : 'Upgrade to read the full summary'}
                </button>
              </>
            );
          }
          return <p className="tcx-p">{text}</p>;
        })())}

        {trlSec?.mode === 'locked' ? (
          <Locked lines={2} label={lockLabel('trl', trlSec.lock)} onClick={() => onLocked('trl', trlSec.lock)} />
        ) : (trlSec || trl) && trl ? (
          <div className="tcx-block" data-trl-category={trl.category || undefined}>
            <p className="tcx-trl-title">{trl.title || (trl.level ? `TRL ${trl.level}` : 'TRL not found')}</p>
            {trl.description && <p className="tcx-trl-desc">{trl.description}</p>}
            {trl.note && <p className="tcx-trl-note">{trl.note}</p>}
          </div>
        ) : null}

        {levers && (
          <div>
            <h4 className="tcx-h4">Value Levers</h4>
            {levers.mode === 'locked' ? (
              <Locked lines={2} label={lockLabel('value_levers', levers.lock)} onClick={() => onLocked('value_levers', levers.lock)} />
            ) : (() => {
              const tags = leverTags(levers.data?.items || []);
              return tags.length
                ? <ul className="tcx-chips">{tags.map((t, i) => <li key={`${i}:${t}`} className="tcx-chip">{t}</li>)}</ul>
                : <p className="tcx-muted">Value levers for this technology are being prepared.</p>;
            })()}
          </div>
        )}

        {similar && (
          <div>
            <h4 className="tcx-h4">Similar Technologies</h4>
            {similar.mode === 'locked' ? (
              <Locked lines={3} label={lockLabel('similar_tech', similar.lock)} onClick={() => onLocked('similar_tech', similar.lock)} />
            ) : (() => {
              const items = ((similar.data?.items || []) as SimilarCardItem[]).filter(it => it && it.title).slice(0, 3);
              if (!items.length) return <p className="tcx-muted">Similar technologies are being prepared.</p>;
              return (
                <ul className="tcx-similar">
                  {items.map((it, i) => {
                    const click = (e: React.MouseEvent) => {
                      tcTrack('techcard_similar_click', { tech_id: techId, target_id: it.id || it.path || null, rank: i + 1, surface });
                      // Inside the platform the similar tech opens here; the
                      // href still serves a middle-click / new tab.
                      if (onOpenTech && it.id && !(e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1)) {
                        e.preventDefault();
                        onOpenTech(it.id);
                      }
                    };
                    return (
                      <li key={`${i}:${it.id || it.path || it.title}`}>
                        {it.path ? (
                          <a href={it.path} target="_blank" rel="noopener noreferrer" onClick={click}>{it.title}</a>
                        ) : (
                          <span style={{ fontSize: 14 }}>{it.title}</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              );
            })()}
          </div>
        )}
      </>
    );
  };

  const renderResearchersTab = () => {
    const s = section('researchers_ip');
    if (!s) return null;
    if (s.mode === 'locked') return <Locked lines={6} label={lockLabel('researchers_ip', s.lock)} onClick={() => onLocked('researchers_ip', s.lock)} />;
    const d = s.data || {};
    const names: string[] = (d.researchers || []).filter(Boolean);
    if (!names.length && !d.ip_summary && !d.publications_summary) {
      return <p className="tcx-muted">No researcher or IP details have been published for this technology.</p>;
    }
    return (
      <>
        {names.length > 0 && (
          <div>
            <h4 className="tcx-h4">Researchers</h4>
            <ul className="tcx-chips">{names.map((n, i) => <li key={`${i}:${n}`} className="tcx-chip tcx-chip-person">{n}</li>)}</ul>
          </div>
        )}
        {d.ip_summary && (
          <div className="tcx-block">
            <h4 className="tcx-h4">IP Summary</h4>
            <p className="tcx-p">{d.ip_summary}</p>
          </div>
        )}
        {d.publications_summary && (
          <div className="tcx-block">
            <h4 className="tcx-h4">Publications Summary</h4>
            <p className="tcx-p">{d.publications_summary}</p>
          </div>
        )}
      </>
    );
  };

  const renderProblemTab = () => {
    const s = section('problem_applications');
    if (!s) return null;
    if (s.mode === 'locked') return <Locked lines={6} label={lockLabel('problem_applications', s.lock)} onClick={() => onLocked('problem_applications', s.lock)} />;
    const d = s.data || {};
    const apps: string[] = (d.example_applications || []).filter(Boolean);
    const tags: string[] = (d.problem_tags || []).filter(Boolean).slice(0, 5);
    if (!d.problem_addressed && !d.problem_evidence && !apps.length && !tags.length) {
      return <p className="tcx-muted">Problem and application insights for this technology are being prepared.</p>;
    }
    return (
      <>
        {d.problem_addressed && (
          <div className="tcx-block">
            <h4 className="tcx-h4">Problem Addressed</h4>
            <p className="tcx-p">{d.problem_addressed}</p>
          </div>
        )}
        {d.problem_evidence && (
          <div className="tcx-block">
            <h4 className="tcx-h4">Problem Evidence</h4>
            <p className="tcx-p">{d.problem_evidence}</p>
          </div>
        )}
        {tags.length > 0 && (
          <div>
            <h4 className="tcx-h4">Problem Tags</h4>
            <ul className="tcx-chips">{tags.map((t, i) => <li key={`${i}:${t}`} className="tcx-chip tcx-chip-tag">{t}</li>)}</ul>
          </div>
        )}
        {apps.length > 0 && (
          <div>
            <h4 className="tcx-h4">Example Applications</h4>
            <ul className="tcx-list">{apps.map((a, i) => <li key={`${i}:${a}`}>{a}</li>)}</ul>
          </div>
        )}
      </>
    );
  };

  // ---- header pieces ----
  const stop = (e: React.MouseEvent) => e.stopPropagation();
  const uniNode = !uniName ? null
    : onUniversityClick ? (
      <button type="button" className="tcx-linkish" onClick={e => {
        stop(e);
        tcTrack('techcard_university_click', { tech_id: techId, surface, via: 'filter' });
        onUniversityClick({ code: uni.code, name: uniName });
      }}>{uniName}</button>
    ) : uni.path ? (
      <a href={uni.path} target="_blank" rel="noopener noreferrer" onClick={e => {
        stop(e);
        tcTrack('techcard_university_click', { tech_id: techId, surface, via: 'page' });
      }}>{uniName}</a>
    ) : <span>{uniName}</span>;
  const countryNode = !country ? null
    : onCountryClick ? (
      <button type="button" className="tcx-linkish" onClick={e => {
        stop(e);
        tcTrack('techcard_country_click', { tech_id: techId, surface });
        onCountryClick(country);
      }}>{country}</button>
    ) : <span>{country}</span>;

  const bodyId = `tcx-body-${cssId}`;
  const TitleTag = isModal ? 'h2' : 'h3';

  return (
    <div
      ref={rootRef}
      className={`tcx tcx-card${open ? ' is-open' : ''}${isModal ? ' tcx-modal' : ''}${isModal && onBack ? ' has-back' : ''}`}
      data-tech-id={techId}
      role={isModal ? 'dialog' : undefined}
      aria-modal={isModal ? true : undefined}
      aria-labelledby={isModal ? `tcx-title-${cssId}` : undefined}
    >
      {isModal && (
        // Pinned to the top of the scrolling card (a zero-height sticky bar),
        // so × — and Back — stay in reach however far down the reader is. In
        // the header they scrolled away, and on a phone the card fills the
        // screen, leaving no backdrop to tap: no way out at all.
        <div className="tcx-closebar">
          {onBack && (
            <button type="button" className="tcx-back" onClick={e => {
              stop(e);
              tcTrack('techcard_back', { tech_id: techId, surface });
              onBack();
            }} title={backLabel ? `Back to ${backLabel}` : 'Back'}>
              <span aria-hidden="true">←</span> Back{backLabel ? <span className="tcx-back-label">{` to ${backLabel}`}</span> : null}
            </button>
          )}
          <button type="button" className="tcx-close" aria-label="Close" onClick={e => {
            stop(e);
            tcTrack('techcard_close', { tech_id: techId, surface });
            onClose?.();
          }}>×</button>
        </div>
      )}
      <div className="tcx-head" onClick={toggle}>
        <div className="tcx-head-main">
          <TitleTag className="tcx-title" id={`tcx-title-${cssId}`}>
            {isModal ? title : (
              <button type="button" className="tcx-title-btn" aria-expanded={open} aria-controls={bodyId}
                onClick={e => { stop(e); toggle(); }}>{title}</button>
            )}
          </TitleTag>
          {(uniNode || countryNode) && (
            <div className="tcx-meta">
              {uniNode}
              {uniNode && countryNode && <span aria-hidden="true">·</span>}
              {countryNode}
            </div>
          )}
        </div>
        <div className="tcx-pills">
          {trlKnown && <span className="tcx-pill" title={data?.trl?.note || undefined}>{trlPill}</span>}
          {relPct !== null && (
            <span className="tcx-pill tcx-pill-rel"
              title={relevanceKind === 'keyword' ? 'Keyword match: how many of your search words appear in this technology, and where'
                : relevanceKind === 'ai' ? 'AI relevance: how well this technology answers your search' : undefined}>
              Relevance: {relPct}%
            </span>
          )}
        </div>
        {!isModal && <span className="tcx-chev" aria-hidden="true"><ChevronIcon /></span>}
        {!open && oneLine && <p className="tcx-oneline" title={oneLine}>{oneLine}</p>}
      </div>

      {open && (
        <div className="tcx-body" id={bodyId}>
          {loadState === 'ready' && data ? (
            <>
              <div className="tcx-tabs" role="tablist" aria-label="Technology details" onKeyDown={onTabKey}>
                {tabs.map(t => (
                  <button
                    key={t}
                    id={`tcx-tab-${cssId}-${t}`}
                    type="button"
                    role="tab"
                    className="tcx-tab"
                    aria-selected={activeTab === t}
                    aria-controls={`tcx-panel-${cssId}`}
                    tabIndex={activeTab === t ? 0 : -1}
                    onClick={() => pickTab(t)}
                  >{TAB_LABELS[t]}</button>
                ))}
              </div>
              <div className="tcx-panel" role="tabpanel" id={`tcx-panel-${cssId}`} aria-labelledby={`tcx-tab-${cssId}-${activeTab}`}>
                {activeTab === 'summary' ? renderSummaryTab()
                  : activeTab === 'researchers_ip' ? renderResearchersTab()
                  : renderProblemTab()}
              </div>
            </>
          ) : loadState === 'error' ? (
            <div className="tcx-err" role="alert">
              <span>{loadError || 'Could not load this technology’s details.'}</span>
              <button type="button" onClick={() => load(true)}>Try again</button>
            </div>
          ) : (
            <div aria-busy="true" aria-label="Loading technology details" style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 4 }}>
              <span className="tcx-skel" style={{ width: 330, maxWidth: '100%', height: 34, borderRadius: 999 }} />
              <span className="tcx-skel" style={{ width: '96%', marginTop: 8 }} />
              <span className="tcx-skel" style={{ width: '92%' }} />
              <span className="tcx-skel" style={{ width: '70%' }} />
              <span className="tcx-skel" style={{ width: '40%', height: 44, marginTop: 6 }} />
            </div>
          )}

          {(loadState === 'ready' || loadState === 'error') && (
            <div className="tcx-foot">
              {ctas.contact_university && (
                <button type="button" className="tcx-btn tcx-btn-primary" onClick={contact} disabled={contactDisabled}>
                  {contactLabel || 'Contact University'}
                </button>
              )}
              {ctas.ip_assessment && (
                <button type="button" className="tcx-btn tcx-btn-beige" onClick={openIpIntel}>Get IP Intel Report</button>
              )}
              {(ctas.share || ctas.bookmark) && (
                <div className="tcx-foot-right">
                  {ctas.share && <ShareMenu techId={data?.id || techId} title={title} surface={surface} onToast={showToast} />}
                  {ctas.bookmark && (
                    <button
                      type="button"
                      className="tcx-icon-btn"
                      aria-label={loggedOut ? 'Sign in to bookmark this technology' : isSaved ? 'Remove bookmark' : 'Bookmark this technology'}
                      aria-pressed={loggedOut ? undefined : isSaved}
                      title={loggedOut ? 'Sign in to bookmark' : isSaved ? 'Bookmarked' : 'Bookmark'}
                      onClick={e => { stop(e); void toggleBookmark(); }}
                      disabled={saving}
                    >
                      <BookmarkIcon filled={!loggedOut && isSaved} />
                      {loggedOut && <span className="tcx-lock-badge"><LockGlyph size={9} /></span>}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {ipOpen && (
        <IpIntelOrderModal techId={data?.id || techId} techTitle={title} entry="techcard" onClose={() => setIpOpen(false)} />
      )}
      {toast}
    </div>
  );
};

export default TechAccordionCard;
